import type { OnboardingStepResult } from "./seller-setup-types";

/**
 * Maps Stripe requirement strings to human-friendly messages.
 * Do not show raw requirement strings to users.
 */
const REQUIREMENT_MESSAGES: Record<string, string> = {
  // Step 2 — Individual
  "individual.first_name": "Add your legal first name",
  "individual.last_name": "Add your legal last name",
  "individual.dob.day": "Add your date of birth",
  "individual.dob.month": "Add your date of birth",
  "individual.dob.year": "Add your date of birth",
  "individual.address.line1": "Add your address",
  "individual.address.city": "Add your city",
  "individual.address.state": "Add your state",
  "individual.address.postal_code": "Add your postal code",
  "individual.phone": "Add your phone number",
  "individual.email": "Add your email",
  "individual.id_number": "Provide your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)",
  "individual.ssn_last_4": "Provide your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)",
  "individual.ssn_full": "Provide your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)",
  "individual.verification.document": "Upload an ID to verify your account",
  "individual.verification.additional_document": "Upload additional verification document",
  // Step 2 — Business
  "company.name": "Add your business legal name",
  "company.address.line1": "Add your business address",
  "company.address.city": "Add your business city",
  "company.address.state": "Add your business state",
  "company.address.postal_code": "Add your business postal code",
  "company.phone": "Add your business phone",
  "company.tax_id": "Add your business tax ID (EIN)",
  "company.verification.document": "Upload business verification document",
  "company.owners_provided": "Add representative details",
  "relationship.representative": "Add representative details",
  // Legal guardian (required when account holder is under 18)
  "person.legal_guardian_address": "Add your legal guardian's address",
  "person.legal_guardian_dob": "Add your legal guardian's date of birth",
  "person.legal_guardian_name": "Add your legal guardian's name",
  "person.legal_guardian_ssn_last_4": "Add your legal guardian's SSN last 4 digits",
  "person.legal_guardian_terms_of_service_acceptance": "Your legal guardian must accept the terms of service",
  "legal_guardian_address": "Add your legal guardian's address",
  "legal_guardian_dob": "Add your legal guardian's date of birth",
  "legal_guardian_name": "Add your legal guardian's name",
  "legal_guardian_ssn_last_4": "Add your legal guardian's SSN last 4 digits",
  "legal_guardian_terms_of_service_acceptance": "Your legal guardian must accept the terms of service",
  // Step 3 — Payout
  external_account: "Add a bank account to receive payouts",
  bank_account: "Add a bank account to receive payouts",
  account_number: "Add a bank account to receive payouts",
  account_holder_name: "Add account holder name",
  // Document uploads (user must take action)
  "documents.bank_account_ownership_verification.files": "Upload documents to verify bank account ownership",
  // Step 4 — ToS
  "tos_acceptance.date": "Accept the terms to finish setup",
  "tos_acceptance.ip": "Accept the terms to finish setup",
  "tos_acceptance.user_agent": "Accept the terms to finish setup",
  terms_of_service: "Accept the terms to finish setup",
};

/**
 * Converts a single Stripe requirement key to a human-friendly message.
 * Exported so Step 5 can display actual requirements when messages array is empty.
 */
export function getMessageForRequirement(req: string): string {
  // Exact match
  if (REQUIREMENT_MESSAGES[req]) return REQUIREMENT_MESSAGES[req];
  // Prefix match for nested keys (e.g. individual.address.*)
  for (const [key, message] of Object.entries(REQUIREMENT_MESSAGES)) {
    if (key.endsWith(".*") && req.startsWith(key.slice(0, -1))) return message;
    if (req.startsWith(key)) return message;
  }
  // Step 2 — generic individual (SSN/ID before generic to get specific message)
  if (req.includes("ssn") || req.includes("id_number")) return "Provide your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)";
  if (req.startsWith("individual.")) return "Add your legal name and address";
  if (req.startsWith("individual.verification")) return "Upload an ID to verify your account";
  if (req.startsWith("company.")) return "Add your business details";
  if (req.startsWith("company.verification")) return "Upload business verification document";
  if (req.includes("legal_guardian")) return "Add your legal guardian as account representative (required for users under 18). Complete this on Stripe's verification page.";
  if (req.includes("person") || req.includes("relationship")) return "Add representative details";
  // Document uploads (must check before generic bank)
  if (req.includes("documents.") || req.includes(".files")) return "Upload documents to verify your information";
  // Step 3
  if (req.includes("external_account") || req.includes("bank")) return "Add a bank account to receive payouts";
  // Step 4
  if (req.includes("tos_acceptance") || req.includes("terms_of_service")) return "Accept the terms to finish setup";
  return "Additional verification required";
}

/** Check if any requirements are legal guardian related (minor account) */
export function hasLegalGuardianRequirements(
  currentlyDue: string[] = [],
  pastDue: string[] = []
): boolean {
  const all = [...pastDue, ...currentlyDue];
  return all.some((r) => r.toLowerCase().includes("legal_guardian"));
}

/** Maps pending_verification keys to friendly "what Stripe is verifying" messages */
const PENDING_VERIFICATION_LABELS: Record<string, string> = {
  external_account: "bank account",
  bank_account: "bank account",
  account_number: "bank account",
  "individual.ssn_last_4": "Social Security Number (SSN) or ITIN",
  "individual.ssn_full": "Social Security Number (SSN) or ITIN",
  "individual.id_number": "Social Security Number (SSN) or ITIN",
  "individual.verification.document": "ID document",
  "individual.verification.additional_document": "ID document",
  "company.tax_id": "business tax ID",
  "company.verification.document": "business verification",
};

function getPendingVerificationLabel(req: string): string {
  if (PENDING_VERIFICATION_LABELS[req]) return PENDING_VERIFICATION_LABELS[req];
  if (req.includes("ssn") || req.includes("id_number")) return "Social Security Number (SSN) or ITIN";
  if (req.includes("external_account") || req.includes("bank") || req.includes("account_number")) return "bank account";
  if (req.includes("verification") || req.includes("document")) return "ID document";
  if (req.includes("individual")) return "personal information";
  if (req.includes("company")) return "business information";
  return "your information";
}

/**
 * Returns human-friendly messages for what Stripe is currently verifying (pending_verification).
 */
export function getPendingVerificationMessages(pendingVerification: string[] = []): string[] {
  const labels = pendingVerification.map(getPendingVerificationLabel);
  return Array.from(new Set(labels));
}

/** Requirement keys that Stripe verifies in the background (user may have already submitted) */
const VERIFICATION_BACKGROUND_KEYS = [
  "ssn",
  "id_number",
  "external_account",
  "bank_account",
  "account_number",
  "verification",
];

/** Requirements that require USER action (upload documents, etc.) - NOT background verification */
export function requiresUserAction(req: string): boolean {
  const lower = req.toLowerCase();
  return lower.includes("documents.") || lower.includes(".files");
}

/** Check if any requirements need document upload (use Stripe Account Link) */
export function hasDocumentUploadRequirements(
  currentlyDue: string[] = [],
  pastDue: string[] = []
): boolean {
  const all = [...pastDue, ...currentlyDue];
  return all.some(requiresUserAction);
}

/** Check if all due requirements are verification-type (SSN, bank, ID) - Stripe may be processing these. Excludes document uploads (user must act). */
export function isVerificationOnlyRequirements(
  currentlyDue: string[] = [],
  pastDue: string[] = []
): boolean {
  const all = [...pastDue, ...currentlyDue];
  if (all.length === 0) return false;
  return all.every((r) => {
    if (requiresUserAction(r)) return false;
    const lower = r.toLowerCase();
    return VERIFICATION_BACKGROUND_KEYS.some((k) => lower.includes(k));
  });
}

/**
 * Returns human-friendly messages for Stripe requirement keys (currently_due + past_due).
 * Use this on Step 5 to show the user exactly what is required.
 */
export function getRequirementMessagesForDisplay(
  currentlyDue: string[] = [],
  pastDue: string[] = []
): string[] {
  const all = [...pastDue, ...currentlyDue];
  const messages = all.map(getMessageForRequirement);
  return Array.from(new Set(messages));
}

/** Step 2 requirement prefixes */
const STEP2_PREFIXES = [
  "individual.",
  "company.",
  "relationship.",
  "person",
  "persons.",
];

/** Step 3 requirement indicators */
const STEP3_INDICATORS = ["external_account", "bank_account", "account_number", "account_holder_name"];

/** Step 4 requirement indicators */
const STEP4_INDICATORS = ["tos_acceptance", "terms_of_service"];

function requirementBelongsToStep(req: string, step: 2 | 3 | 4): boolean {
  if (step === 2) return STEP2_PREFIXES.some((p) => req.startsWith(p) || req.includes(p));
  if (step === 3) return STEP3_INDICATORS.some((s) => req.includes(s));
  if (step === 4) return STEP4_INDICATORS.some((s) => req.includes(s));
  return false;
}

/**
 * Maps Stripe requirements to onboarding step and human-friendly messages.
 * Priority: past_due > currently_due > payouts_enabled => active > pending.
 */
export function getOnboardingStepFromStripeRequirements(
  currentlyDue: string[],
  pastDue: string[],
  disabledReason: string | null,
  payoutsEnabled: boolean
): OnboardingStepResult {
  const allDue = [...pastDue, ...currentlyDue];
  const messages = Array.from(new Set(allDue.map(getMessageForRequirement)));

  if (payoutsEnabled && allDue.length === 0) {
    return { stepNumber: 5, messages: [], status: "active" };
  }

  if (pastDue.length > 0) {
    const step2 = pastDue.find((r) => requirementBelongsToStep(r, 2));
    const step3 = pastDue.find((r) => requirementBelongsToStep(r, 3));
    const step4 = pastDue.find((r) => requirementBelongsToStep(r, 4));
    if (step4) return { stepNumber: 4, messages, status: "action_required" };
    if (step3) return { stepNumber: 3, messages, status: "action_required" };
    if (step2) return { stepNumber: 2, messages, status: "action_required" };
    return { stepNumber: 5, messages, status: "action_required" };
  }

  if (currentlyDue.length > 0) {
    const step2 = currentlyDue.find((r) => requirementBelongsToStep(r, 2));
    const step3 = currentlyDue.find((r) => requirementBelongsToStep(r, 3));
    const step4 = currentlyDue.find((r) => requirementBelongsToStep(r, 4));
    if (step4) return { stepNumber: 4, messages, status: "action_required" };
    if (step3) return { stepNumber: 3, messages, status: "action_required" };
    if (step2) return { stepNumber: 2, messages, status: "action_required" };
    return { stepNumber: 5, messages, status: "action_required" };
  }

  if (disabledReason) {
    return { stepNumber: 5, messages: [disabledReason], status: "action_required" };
  }

  return { stepNumber: 5, messages: [], status: "pending" };
}
